import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Copy, Plus, Search } from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { useVbsCalculationListQuery } from "@/services/backend/vbs/calculations/api/calculation-api";
import { v4 } from "uuid";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { ProcessTopic } from "@/services/backend/process-topic/components/process-topic";
import { ReactNode, useEffect, useState } from "react";
import {
  useVbsOfferCreateFromCalculationMutation,
  useVbsOfferCreateMutation,
  useVbsOfferListQuery,
} from "@/services/backend/vbs/offers/api/offer-api";
import { Calculation } from "@/services/backend/vbs/calculations/types/calculation";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { CopyOfferButton } from "@/services/backend/vbs/offers/components/copy-offer-button";
import { FilterMode } from "@/shared/lib/filter/filter";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { BranchSelectFrom } from "@/services/backend/branch/components/branch-select-from";
import { ServiceObjectSelectFrom } from "@/services/backend/customers/components/service-object-select-from";

interface OfferRef {
  id: string;
  processId: string | null;
}

interface Props {
  processId: string;
  onCreated: (offerId: string) => void;
  renderCalculationLink: (
    calculationId: string,
    children: ReactNode,
  ) => ReactNode;
  renderOfferLink: (offer: OfferRef, children: ReactNode) => ReactNode;
  trigger?: ReactNode;
}

export function CreateOfferDialog({
  processId,
  onCreated,
  renderCalculationLink,
  renderOfferLink,
  trigger,
}: Props) {
  const [open, setOpen] = useState(false);

  const closeDialogAndNavigate = (offerId: string) => {
    setOpen(false);
    onCreated(offerId);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm">
            <Plus className="h-5 w-5" />
            <span>{t("Angebot")}</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] min-w-[80vw] overflow-y-scroll">
        <DialogHeader>
          <DialogTitle>{t("Neues Angebot erstellen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Angebote können entweder komplett neu, auf Basis einer Kalkulation oder auf Basis eines alten Angebots erstellt werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="manual">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="manual">{t("Neu Anlegen")}</TabsTrigger>
            <TabsTrigger value="from-calculation">
              {t("Kalkulationen")}
            </TabsTrigger>
            <TabsTrigger value="from-old">{t("Alte Angebote")}</TabsTrigger>
          </TabsList>
          <TabsContent value="manual">
            <CreateManually
              processId={processId}
              onCreated={closeDialogAndNavigate}
            />
          </TabsContent>
          <TabsContent value="from-calculation">
            <CreateFromCalculation
              processId={processId}
              onCreated={closeDialogAndNavigate}
              renderCalculationLink={renderCalculationLink}
            />
          </TabsContent>
          <TabsContent value="from-old">
            <CreateFromOld
              processId={processId}
              onCreated={closeDialogAndNavigate}
              renderOfferLink={renderOfferLink}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function CreateManually({
  processId,
  onCreated,
}: {
  processId: string;
  onCreated: (calculationId: string) => void;
}) {
  const [id, setId] = useState(v4());
  const [create, { isLoading, error, reset, isSuccess }] =
    useVbsOfferCreateMutation();
  const { data: process } = useProcessesProcessShowQuery({ id: processId });

  const branches = process?.branches ?? [];
  const serviceObjects = process?.serviceObjects ?? [];
  const [branchId, setBranchId] = useState(branches[0] ?? "");
  const [serviceObjectId, setServiceObjectId] = useState(
    serviceObjects[0] ?? "",
  );

  if (isSuccess) {
    onCreated(id);
    reset();
    setId(v4());
  }

  const disabled =
    isLoading ||
    !process ||
    !process.customerId ||
    branchId === "" ||
    serviceObjectId === "";

  const onClick = () => {
    if (disabled) {
      return;
    }

    create({
      id,
      processId: process.id,
      companyId: process.companyId,
      branchId,
      customerId: process.customerId!,
      serviceObjectId,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Neu Anlegen")}</CardTitle>
        <CardDescription>
          {t(
            "Erstellt eine Angebot mit den Basisdaten des Prozesses und des Kunden.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {branches.length !== 1 && (
          <BranchSelectFrom
            branchIds={branches}
            value={branchId}
            onValueChange={setBranchId}
          />
        )}
        {serviceObjects.length !== 1 && (
          <ServiceObjectSelectFrom
            serviceObjectIds={serviceObjects}
            customerId={process?.customerId}
            value={serviceObjectId}
            onValueChange={setServiceObjectId}
          />
        )}
        <RTKQueryErrorAlert error={error} />
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={onClick} disabled={disabled}>
          {t("Erstellen")}
        </Button>
      </CardFooter>
    </Card>
  );
}

function CreateFromCalculation({
  processId,
  onCreated,
  renderCalculationLink,
}: {
  processId: string;
  onCreated: (calculationId: string) => void;
  renderCalculationLink: (
    calculationId: string,
    children: ReactNode,
  ) => ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Auf Basis alter Kalkulation")}</CardTitle>
        <CardDescription>
          {t(
            "Kopiert die Daten der alten Kalkulation. Es werden nur Kalkulationen von abgeschlossenen Vorgängen angezeigt.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <CalculationsTable
          processId={processId}
          onCreated={onCreated}
          renderCalculationLink={renderCalculationLink}
        />
      </CardContent>
    </Card>
  );
}

function CalculationsTable({
  processId,
  onCreated,
  renderCalculationLink,
}: {
  processId: string;
  onCreated: (calculationId: string) => void;
  renderCalculationLink: (
    calculationId: string,
    children: ReactNode,
  ) => ReactNode;
}) {
  const {
    data: list,
    isLoading,
    error,
  } = useVbsCalculationListQuery({
    process: {
      active: true,
      values: [processId],
    },
    owner: {
      active: true,
      mode: FilterMode.IsNull,
    },
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Table className="animate-pulse">
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">{t("Aktuelle")}</TableHead>
            <TableHead>{t("Nummer")}</TableHead>
            <TableHead>{t("Erstellt am")}</TableHead>
            <TableHead className="w-56">{t("Aktionen")}</TableHead>
          </TableRow>
        </TableHeader>
      </Table>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead>{t("Erstellt am")}</TableHead>
          <TableHead className="w-56">{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...(list?.items ?? [])].sort(byCreatedAt).map((calculation) => (
          <TableRow key={calculation.id}>
            <TableCell>
              <DocumentNumberById documentId={calculation.id} />
            </TableCell>
            <TableCell>
              {new Date(calculation.createdAt).toLocaleDateString()}
            </TableCell>
            <TableCell>
              <div className="flex space-x-2">
                {renderCalculationLink(
                  calculation.id,
                  <Button variant="outline" size="sm">
                    <Search className="h-4 w-4" />
                  </Button>,
                )}
                <CreateOfferFromCalculationButton
                  calculationId={calculation.id}
                  processId={processId}
                  onCreated={onCreated}
                />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function byCreatedAt(a: Calculation, b: Calculation) {
  return a.createdAt.localeCompare(b.createdAt);
}

function CreateOfferFromCalculationButton(props: {
  calculationId: string;
  processId: string;
  onCreated: (offerId: string) => void;
}) {
  const { calculationId, processId, onCreated } = props;
  const [create, { isLoading, error, reset }] =
    useVbsOfferCreateFromCalculationMutation();
  const { data: process } = useProcessesProcessShowQuery({ id: processId });

  const branches = process?.branches ?? [];
  const serviceObjects = process?.serviceObjects ?? [];
  const [branchId, setBranchId] = useState(branches[0] ?? "");
  const [serviceObjectId, setServiceObjectId] = useState(
    serviceObjects[0] ?? "",
  );

  const disabled =
    isLoading ||
    !process ||
    !process.customerId ||
    branchId === "" ||
    serviceObjectId === "";

  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({ ...parseRTKQueryError(error), variant: "destructive" });
      reset();
    }
  }, [error, toast, reset]);

  const onClick = () => {
    if (disabled) {
      return;
    }
    const id = v4();
    create({
      calculationId,
      id,
      processId: process.id,
      companyId: process.companyId,
      branchId,
      customerId: process.customerId!,
      serviceObjectId,
    })
      .unwrap()
      .then(() => onCreated(id));
  };

  return (
    <div className="flex flex-col items-end gap-2">
      {branches.length !== 1 && (
        <BranchSelectFrom
          branchIds={branches}
          value={branchId}
          onValueChange={setBranchId}
        />
      )}
      {serviceObjects.length !== 1 && (
        <ServiceObjectSelectFrom
          serviceObjectIds={serviceObjects}
          customerId={process?.customerId}
          value={serviceObjectId}
          onValueChange={setServiceObjectId}
        />
      )}
      <Button onClick={onClick} disabled={disabled} size="sm">
        <Copy className="mr-3 h-4 w-4" />
        <span>{t("Daten Übernehmen")}</span>
      </Button>
    </div>
  );
}

function CreateFromOld(props: {
  processId: string;
  onCreated: (offerId: string) => void;
  renderOfferLink: (offer: OfferRef, children: ReactNode) => ReactNode;
}) {
  const { processId, onCreated, renderOfferLink } = props;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Auf Basis eines alten Angebots")}</CardTitle>
        <CardDescription>
          {t(
            "Kopiert die Daten des alten Angebots. Es werden nur Angebote von abgeschlossenen Vorgängen angezeigt.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <OldOfferTable
          processId={processId}
          onCreated={onCreated}
          renderOfferLink={renderOfferLink}
        />
      </CardContent>
    </Card>
  );
}

function OldOfferTable({
  processId,
  onCreated,
  renderOfferLink,
}: {
  processId: string;
  onCreated: (offerId: string) => void;
  renderOfferLink: (offer: OfferRef, children: ReactNode) => ReactNode;
}) {
  const { data: process } = useProcessesProcessShowQuery({ id: processId });

  const {
    data: list,
    isLoading,
    error,
  } = useVbsOfferListQuery({
    customer: {
      active: true,
      values: [process?.customerId ?? ""],
    },
    immutable: {
      active: true,
      values: [true],
    },
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Table className="animate-pulse">
        <TableHeader>
          <TableRow>
            <TableHead>{t("Datum")}</TableHead>
            <TableHead>{t("Nummer")}</TableHead>
            <TableHead className="w-full">{t("Vorgang Thema")}</TableHead>
            <TableHead>{t("Aktionen")}</TableHead>
          </TableRow>
        </TableHeader>
      </Table>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Datum")}</TableHead>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead className="w-full">{t("Vorgang Thema")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {list?.items?.map((offer) => (
          <TableRow key={offer.id}>
            <TableCell>
              {new Date(offer.offerDate).toLocaleDateString()}
            </TableCell>
            <TableCell>
              <DocumentNumberById documentId={offer.id} />
            </TableCell>
            <TableCell>
              <ProcessTopic processId={processId} />
            </TableCell>
            <TableCell>
              <div className="flex space-x-2">
                <NavigateToOffer
                  currentProcessId={processId}
                  offer={offer}
                  renderOfferLink={renderOfferLink}
                />
                <CopyOfferButton
                  offerId={offer.id}
                  processId={processId}
                  onCopy={onCreated}
                />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function NavigateToOffer(props: {
  currentProcessId: string;
  offer: OfferRef;
  renderOfferLink: (offer: OfferRef, children: ReactNode) => ReactNode;
}) {
  const { currentProcessId, offer, renderOfferLink } = props;

  // If there is no offer processId, we cannot navigate to the offer.
  if (!offer.processId) {
    return null;
  }

  // If the offer is part of the same process,
  // simply navigate to the offer
  if (currentProcessId === offer.processId) {
    return renderOfferLink(
      offer,
      <Button variant="outline" size="sm">
        <Search className="h-4 w-4" />
      </Button>,
    );
  }

  // If the offer is part of a different process,
  // show a warning and then navigate to the offer
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Search className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Achtung")}</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {t(
            "Dieses Angebot ist Teil eines anderen Vorgangs. Sie müssen nachdem Sie das Angbot angesehen haben zum aktuellen Vorgang zurückkehren.",
          )}
        </DialogDescription>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          {renderOfferLink(offer, <Button>{t("Vorgang wechseln")}</Button>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
